import type { ReactNode } from "react";
import { isApiError } from "../api/client";
import { useBrandName } from "../lib/brand";
import { cx } from "../lib/cx";
import { Button } from "./Button";
import { IconAlertCircle, IconRefresh } from "./icons";

/** An honest empty state: what's here (nothing), and the next action. */
export function EmptyState({
  icon,
  title,
  children,
  action,
  className,
  compact,
  heading: Heading = "h3",
}: {
  icon?: ReactNode;
  title: ReactNode;
  children?: ReactNode;
  action?: ReactNode;
  className?: string;
  compact?: boolean;
  /** The title's level: an h1 when the empty state is the whole page (the 404). */
  heading?: "h1" | "h2" | "h3";
}) {
  return (
    <div
      className={cx(
        "flex flex-col items-center rounded-[var(--radius-card)] text-center",
        compact ? "px-6 py-10" : "px-6 py-16 sm:py-20",
        className,
      )}
    >
      {icon ? (
        <div className="mb-3.5 inline-flex h-11 w-11 items-center justify-center rounded-full bg-panel text-dim">
          {icon}
        </div>
      ) : null}
      <Heading className="display text-[19px] leading-snug">{title}</Heading>
      {children ? <div className="mt-2 max-w-md text-[14px] leading-relaxed text-body">{children}</div> : null}
      {action ? <div className="mt-5 flex flex-wrap items-center justify-center gap-2.5">{action}</div> : null}
    </div>
  );
}

/** What went wrong loading something, in plain words, with a retry. */
export function ErrorState({
  error,
  title,
  onRetry,
  className,
  compact,
}: {
  error: unknown;
  title?: string;
  onRetry?: () => void;
  className?: string;
  compact?: boolean;
}) {
  const brand = useBrandName();
  const message = isApiError(error)
    ? error.message
    : "This didn't load. Try again in a moment.";
  const heading =
    title ??
    (isApiError(error) && error.isNetwork
      ? `${brand} isn't reachable`
      : isApiError(error) && error.status === 404
        ? "Not found"
        : "This didn't load");
  return (
    <div
      role="alert"
      className={cx(
        "flex flex-col items-center rounded-[var(--radius-card)] text-center",
        compact ? "px-6 py-8" : "px-6 py-14",
        className,
      )}
    >
      <div className="mb-3.5 inline-flex h-10 w-10 items-center justify-center rounded-full bg-bad-bg text-bad shadow-[inset_0_0_0_1px_var(--hx-bad-line)]">
        <IconAlertCircle size={20} />
      </div>
      <h3 className="text-[15px] font-semibold text-fg">{heading}</h3>
      <p className="mt-1.5 max-w-md text-[14px] leading-relaxed text-body">{message}</p>
      {onRetry ? (
        <Button size="sm" className="mt-4" onClick={onRetry} leading={<IconRefresh size={15} />}>
          Try again
        </Button>
      ) : null}
    </div>
  );
}
